import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, GraduationCap, Star, Users, Clock, Fire, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const LEARNING_CATEGORIES = ["All", "Deep Learning", "NLP / LLMs", "MLOps", "Computer Vision", "Reinforcement Learning", "AI Ethics", "Prompt Engineering", "Data Science", "Course", "Tutorial", "Book", "Workshop"];
const LEARNING_LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-4 mb-3">
        <div className="w-8 h-8 bg-slate-200 rounded shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-3 bg-slate-200 rounded w-2/3 mb-4" />
      <div className="flex gap-2">
        {[1,2,3].map(i => <div key={i} className="h-5 bg-slate-200 rounded-full w-16" />)}
      </div>
    </div>
  );
}

function LearningCard({ resource }) {
  const slug = resource.slug || resource.id;
  const levelColors = {
    Beginner:     "bg-green-50 text-green-700 border-green-200",
    Intermediate: "bg-blue-50 text-blue-700 border-blue-200",
    Advanced:     "bg-purple-50 text-purple-700 border-purple-200",
  };
  const typeIcons = {
    "Course Series":   "📚",
    "Short Course":    "⚡",
    "Course":          "🎓",
    "Tutorial Series": "🔧",
    "Tutorial":        "📖",
    "Book":            "📕",
    "Workshop":        "🛠",
    "Certification":   "🏆",
    "Video Series":    "🎬",
  };

  const isFeatured = resource.featured || resource.isFeatured || resource.is_featured;
  const isNew = resource.isNew || resource.is_new;
  const isHot = resource.hot;
  const price = resource.price || resource.pricing || "Free";
  const url = resource.url || "";
  const topics = resource.topics || resource.tags || [];
  const rating = resource.rating ?? 4.5;
  const students = resource.students || "";

  return (
    <Link to={`/trending-ai/learning/${slug}`} className={`block bg-white border rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group ${
      isFeatured ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"
    }`}>
      {isFeatured && (
        <div className="text-[10px] font-bold text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1 mb-3">
          ⭐ FEATURED
        </div>
      )}

      <div className="flex items-start gap-4 mb-3">
        <div className="text-2xl shrink-0">{typeIcons[resource.type] || "📄"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${levelColors[resource.level] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {resource.level || "All levels"}
            </span>
            {resource.type && <span className="text-[10px] text-slate-400">{resource.type}</span>}
            {isNew && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
            {isHot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
          </div>
          <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors leading-snug">{stripHtml(resource.title)}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{stripHtml(resource.provider)}</p>
        </div>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed mb-4">{stripHtml(resource.description)}</p>

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {topics.slice(0, 5).map((topic) => (
            <span key={topic} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{topic}</span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
        <span className="flex items-center gap-1">
          <Star size={11} className="text-amber-400" weight="fill" /> {rating}
        </span>
        {students && <span className="flex items-center gap-1"><Users size={11} /> {students}</span>}
        {resource.duration && <span className="flex items-center gap-1"><Clock size={11} /> {resource.duration}</span>}
      </div>

      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <div>
          <span className={`text-sm font-bold ${price === "Free" || (price || "").includes("Free") ? "text-emerald-600" : "text-slate-800"}`}>
            {price}
          </span>
          <span className="text-xs text-slate-400 ml-2">{resource.category}</span>
        </div>
        {url && (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg">
            Start learning <ArrowUpRight size={11} />
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AILearningPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [level, setLevel] = useState("All");
  const [onlyFree, setOnlyFree] = useState(false);

  const fetchFn = useCallback(
    (page) => fetchAISection("learning", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-learning",
    getCacheKey: (page) => `ai-learning-p${page}`,
    ttlMs: 15 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems
    .filter((r) => {
      const catVal = r.category || r.type || "";
      const matchCat = cat === "All" || catVal === cat;
      const matchLevel = level === "All" || r.level === level;
      const matchSearch = !search || r.title.toLowerCase().includes(search.toLowerCase()) || (r.provider || "").toLowerCase().includes(search.toLowerCase());
      const price = r.price || r.pricing || "";
      const matchFree = !onlyFree || price.includes("Free") || price === "Free";
      return matchCat && matchLevel && matchSearch && matchFree;
    })
    .sort((a, b) => {
      if ((a.featured || a.isFeatured) && !(b.featured || b.isFeatured)) return -1;
      if (!(a.featured || a.isFeatured) && (b.featured || b.isFeatured)) return 1;
      return (b.rating ?? 4.5) - (a.rating ?? 4.5);
    });

  const freeCount = allItems.filter((r) => {
    const price = r.price || r.pricing || "";
    return price.includes("Free") || price === "Free";
  }).length;

  return (
    <>
      <SEO
        title="AI Learning — Best AI Courses, Tutorials & Guides · TrendMing"
        description="Learn AI with the best courses: Deep Learning Specialization, LangChain, fast.ai, Hugging Face NLP, and more. Free and paid options for all levels."
        canonical="https://trendming.com/trending-ai/learning"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Learning</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Learning Center</h1>
          <p className="text-slate-500 text-sm mt-1">Curated AI courses, tutorials, and guides from the world's best educators.</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "Resources", value: allItems.length },
            { label: "Free Resources", value: freeCount },
            { label: "Showing", value: filtered.length },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search courses…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              {LEARNING_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
              {LEARNING_LEVELS.map((l) => <option key={l}>{l}</option>)}
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-slate-600 whitespace-nowrap">Free only</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((resource) => <LearningCard key={resource.id} resource={resource} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No resources found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load learning resources</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Recommended Learning Paths</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { emoji: "🚀", title: "AI Beginner Path", steps: ["AI for Everyone", "Fast.ai Practical DL", "Prompt Engineering"], duration: "~3 months" },
              { emoji: "🧠", title: "LLM Engineer Path", steps: ["Deep Learning Spec", "Hugging Face NLP", "Building LLM Apps with LangChain"], duration: "~6 months" },
              { emoji: "🤖", title: "AI Agent Path", steps: ["LLM Engineering basics", "LangChain/LangGraph", "Multi-Agent Systems with CrewAI"], duration: "~4 months" },
            ].map((path) => (
              <div key={path.title} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5">
                <div className="text-3xl mb-2">{path.emoji}</div>
                <h3 className="text-sm font-bold text-slate-900 mb-2">{path.title}</h3>
                <ol className="space-y-1.5 mb-3">
                  {path.steps.map((step, i) => (
                    <li key={step} className="flex items-center gap-2 text-xs text-slate-600">
                      <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-[9px] font-bold shrink-0">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
                <span className="text-xs text-blue-600 font-medium">⏱ {path.duration}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
